import { ComponentSize, TextColor } from '../../../types';
import { ButtonColor, ButtonVariant } from './Button.types';

/**
 * Configurations based on the button size.
 */
export const BUTTON_SIZE_CONFIG: Record<ComponentSize, { btnSizeClass: string; iconSize: ComponentSize }> = {
  [ComponentSize.X_SMALL]: {
    btnSizeClass: 'btn-xs',
    iconSize: ComponentSize.X_SMALL,
  },
  [ComponentSize.SMALL]: {
    btnSizeClass: 'btn-sm',
    iconSize: ComponentSize.SMALL,
  },
  [ComponentSize.MEDIUM]: {
    btnSizeClass: 'btn-md',
    iconSize: ComponentSize.MEDIUM,
  },
  [ComponentSize.LARGE]: {
    btnSizeClass: 'btn-lg',
    iconSize: ComponentSize.LARGE,
  },
};

/**
 * Configurations based on the button variant.
 */
export const BUTTON_VARIANT_CONFIG: Record<
  ButtonVariant,
  Partial<Record<ButtonColor, Record<string, { bg: string; text: TextColor | string; shadow?: string }>>>
> = {
  [ButtonVariant.STRONG]: {
    [ButtonColor.PRIMARY]: {
      default: {
        bg: '!bg-primary border-none',
        text: 'text-primary-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.primary/0.5)]',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-content/20  to-base-content/0 !bg-primary border-none',
        text: 'text-primary-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.primary/0.5)]',
      },
      hover: {
        bg: 'bg-gradient-to-b from-base-content/10 to-base-content/0 !bg-primary border-none',
        text: 'text-primary-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.primary/0.5)]',
      },
      disabled: {
        bg: '!bg-primary !bg-opacity-40 border-none pointer-events-none',
        text: 'text-primary-content',
      },
    },
    [ButtonColor.SECONDARY]: {
      default: {
        bg: '!bg-secondary border-none',
        text: 'text-secondary-content',
        shadow: '!shadow-[0_1px_1px_0px_theme(colors.secondary/0.5)]',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-content/20  to-base-content/0 !bg-secondary border-none',
        text: 'text-secondary-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.secondary/0.5)]',
      },
      hover: {
        bg: 'bg-gradient-to-b from-base-content/10 to-base-content/0 !bg-secondary border-none',
        text: 'text-secondary-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.secondary/0.5)]',
      },
      disabled: {
        bg: '!bg-secondary !bg-opacity-40 border-none pointer-events-none',
        text: 'text-secondary-content',
      },
    },
    [ButtonColor.DESTRUCTIVE]: {
      default: {
        bg: '!bg-error border-none',
        text: 'text-error-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.error/0.5)]',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-content/20  to-base-content/0 !bg-error border-none',
        text: 'text-error-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.error/0.5)]',
      },
      hover: {
        bg: 'bg-gradient-to-b from-base-content/10 to-base-content/0 !bg-error border-none',
        text: 'text-error-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.error/0.5)]',
      },
      disabled: {
        bg: '!bg-error !bg-opacity-40 border-none pointer-events-none',
        text: 'text-error-content',
      },
    },
    [ButtonColor.NEUTRAL]: {
      default: {
        bg: '!bg-gray-700 border-none',
        text: 'text-base-200',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.neutral/0.5)]',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-content/20  to-base-content/0 !bg-gray-700 border-none',
        text: 'text-base-200',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.neutral/0.5)]',
      },
      hover: {
        bg: 'bg-gradient-to-b from-base-content/10 to-base-content/0 !bg-gray-700 border-none',
        text: 'text-base-200',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.neutral/0.5)]',
      },
      disabled: {
        bg: 'bg-gray-700 !bg-opacity-40 border-none pointer-events-none',
        text: 'text-base-200',
      },
    },
    [ButtonColor.TERTIARY]: {
      default: {
        bg: '!bg-accent border-none',
        text: 'text-accent-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.accent/0.5)]',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-content/20  to-base-content/0 !bg-accent border-none',
        text: 'text-accent-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.accent/0.5)]',
      },
      hover: {
        bg: 'bg-gradient-to-b from-base-content/10 to-base-content/0 !bg-accent border-none',
        text: 'text-accent-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.accent/0.5)]',
      },
      disabled: {
        bg: 'bg-accent !bg-opacity-40 border-none pointer-events-none',
        text: 'text-accent-content',
      },
    },
    [ButtonColor.GONG]: {
      default: {
        bg: '!bg-gong-purple border-none',
        text: TextColor.INVERSE,
        shadow: '!shadow-[0px_1px_1px_0px_rgba(62,0,117,0.5)]',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-content/20  to-base-content/0 !bg-gong-purple border-none',
        text: TextColor.INVERSE,
        shadow: '!shadow-[0px_1px_1px_0px_rgba(62,0,117,0.5)]',
      },
      hover: {
        bg: 'bg-gradient-to-b from-base-content/10 to-base-content/0 !bg-gong-purple border-none',
        text: TextColor.INVERSE,
        shadow: '!shadow-[0px_1px_1px_0px_rgba(62,0,117,0.5)]',
      },
      disabled: {
        bg: '!bg-gong-purple !bg-opacity-40 border-none pointer-events-none',
        text: TextColor.INVERSE,
      },
    },
    [ButtonColor.SALESLOFT]: {
      default: {
        bg: '!bg-salesloft-teal border-none',
        text: TextColor.INVERSE,
        shadow: '!shadow-[0px_1px_1px_0px_rgba(2,82,75,0.5)]',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-content/20  to-base-content/0 !bg-salesloft-teal border-none',
        text: TextColor.INVERSE,
        shadow: '!shadow-[0px_1px_1px_0px_rgba(2,82,75,0.5)]',
      },
      hover: {
        bg: 'bg-gradient-to-b from-base-content/10 to-base-content/0 !bg-salesloft-teal border-none',
        text: TextColor.INVERSE,
        shadow: '!shadow-[0px_1px_1px_0px_rgba(2,82,75,0.5)]',
      },
      disabled: {
        bg: '!bg-salesloft-teal !bg-opacity-40 border-none pointer-events-none',
        text: TextColor.INVERSE,
      },
    },
    [ButtonColor.NAV_MENU]: {
      default: {
        bg: '!bg-transparent border border-transparent',
        text: 'text-secondary-content',
      },
      active: {
        bg: '!bg-secondary-content/20 !border !border-secondary-content/20 !backdrop-blur-[2px]',
        shadow: '!shadow-[0px_2px_8px_0px_theme(colors.info-content/0.2)]',
        text: 'text-secondary-content',
      },
      hover: {
        bg: '!bg-secondary-content/10 !border !border-secondary-content/10 !backdrop-blur-[2px]',
        shadow: '!shadow-[0px_2px_8px_0px_theme(colors.info-content/0.2)]',
        text: 'text-secondary-content',
      },
      disabled: {
        bg: '!bg-transparent border border-transparent !bg-opacity-40 pointer-events-none',
        text: 'text-secondary-content',
      },
    },
  },
  // Transparent border on active and hover states to avoid layout shifts
  [ButtonVariant.SOFT]: {
    [ButtonColor.PRIMARY]: {
      default: {
        bg: 'btn-outline !bg-primary-content !border-[0.5px] !border-green-100',
        text: 'text-primary',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-content/20  to-base-content/0 !bg-primary !border-[0.5px] !border-transparent',
        text: 'text-primary-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.primary/0.5)]',
      },
      hover: {
        bg: 'bg-gradient-to-b from-base-content/10 to-base-content/0 !bg-primary !border-[0.5px] !border-transparent',
        text: 'text-primary-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.primary/0.5)]',
      },
      disabled: {
        bg: 'btn-outline !bg-primary-content !border-[0.5px] !border-green-100 !bg-opacity-40 pointer-events-none',
        text: 'text-primary !text-opacity-40',
      },
    },
    [ButtonColor.SECONDARY]: {
      default: {
        bg: 'btn-outline !bg-secondary-content !border-[0.5px] !border-blue-100',
        text: 'text-secondary',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-content/20  to-base-content/0 !bg-secondary !border-[0.5px] !border-transparent',
        text: 'text-secondary-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.secondary/0.5)]',
      },
      hover: {
        bg: 'bg-gradient-to-b from-base-content/10 to-base-content/0 !bg-secondary !border-[0.5px] !border-transparent',
        text: 'text-secondary-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.secondary/0.5)]',
      },
      disabled: {
        bg: 'btn-outline !bg-secondary-content !border-[0.5px] !border-blue-100 !bg-opacity-40 pointer-events-none',
        text: 'text-secondary !text-opacity-40',
      },
    },
    [ButtonColor.DESTRUCTIVE]: {
      default: {
        bg: 'btn-outline !bg-error-content !border-[0.5px] !border-red-100',
        text: 'text-error',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-content/20  to-base-content/0 !bg-error !border-[0.5px] !border-transparent',
        text: 'text-error-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.error/0.5)]',
      },
      hover: {
        bg: 'bg-gradient-to-b from-base-content/10 to-base-content/0 !bg-error !border-[0.5px] !border-transparent',
        text: 'text-error-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.error/0.5)]',
      },
      disabled: {
        bg: 'btn-outline !bg-error-content !border-[0.5px] !border-red-100 !bg-opacity-40 pointer-events-none',
        text: 'text-error !text-opacity-40',
      },
    },
    [ButtonColor.NEUTRAL]: {
      default: {
        bg: 'btn-outline !bg-base-200 !border-[0.5px] !border-gray-300',
        text: 'text-neutral',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-content/20  to-base-content/0 !bg-gray-700 !border-[0.5px] !border-transparent',
        text: 'text-base-200',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.neutral/0.5)]',
      },
      hover: {
        bg: 'bg-gradient-to-b from-base-content/10 to-base-content/0 !bg-gray-700 !border-[0.5px] !border-transparent',
        text: 'text-base-200',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.neutral/0.5)]',
      },
      disabled: {
        bg: 'btn-outline !bg-base-200 !border-[0.5px] !border-gray-300 !bg-opacity-40 pointer-events-none',
        text: 'text-neutral !text-opacity-40',
      },
    },
    [ButtonColor.TERTIARY]: {
      default: {
        bg: 'btn-outline !bg-accent-content !border-[0.5px] !border-purple-100',
        text: 'text-accent',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-content/20  to-base-content/0 !bg-accent !border-[0.5px] !border-transparent',
        text: 'text-accent-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.accent/0.5)]',
      },
      hover: {
        bg: 'bg-gradient-to-b from-base-content/10 to-base-content/0 !bg-accent !border-[0.5px] !border-transparent',
        text: 'text-accent-content',
        shadow: '!shadow-[0px_1px_1px_0px_theme(colors.accent/0.5)]',
      },
      disabled: {
        bg: 'btn-outline !bg-accent-content !border-[0.5px] !border-purple-100 !bg-opacity-40 pointer-events-none',
        text: 'text-accent !text-opacity-40',
      },
    },
  },
  [ButtonVariant.GHOST]: {
    [ButtonColor.PRIMARY]: {
      default: {
        bg: '!bg-transparent border-none',
        text: 'text-primary',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-100/20  to-base-100/0 !bg-primary-content border-none',
        text: 'text-primary',
      },
      hover: {
        bg: '!bg-primary-content border-none',
        text: 'text-primary',
      },
      disabled: {
        bg: '!bg-transparent pointer-events-none',
        text: 'text-primary !text-opacity-40',
      },
    },
    [ButtonColor.SECONDARY]: {
      default: {
        bg: '!bg-transparent border-none',
        text: 'text-secondary',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-100/20  to-base-100/0 !bg-secondary-content border-none',
        text: 'text-secondary',
      },
      hover: {
        bg: '!bg-secondary-content border-none',
        text: 'text-secondary',
      },
      disabled: {
        bg: '!bg-transparent pointer-events-none',
        text: 'text-secondary !text-opacity-40',
      },
    },
    [ButtonColor.DESTRUCTIVE]: {
      default: {
        bg: '!bg-transparent border-none',
        text: 'text-error',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-100/20  to-base-100/0 !bg-error-content border-none',
        text: 'text-error',
      },
      hover: {
        bg: '!bg-error-content border-none',
        text: 'text-error',
      },
      disabled: {
        bg: '!bg-transparent pointer-events-none',
        text: 'text-error !text-opacity-40',
      },
    },
    [ButtonColor.NEUTRAL]: {
      default: {
        bg: '!bg-transparent border-none',
        text: 'text-neutral',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-100/20  to-base-100/0 !bg-base-200 border-none',
        text: 'text-neutral',
      },
      hover: {
        bg: '!bg-base-200 border-none',
        text: 'text-neutral',
      },
      disabled: {
        bg: '!bg-transparent pointer-events-none',
        text: 'text-neutral !text-opacity-40',
      },
    },
    [ButtonColor.TERTIARY]: {
      default: {
        bg: '!bg-transparent border-none',
        text: 'text-accent',
      },
      active: {
        bg: 'bg-gradient-to-b from-base-100/20  to-base-100/0 !bg-accent-content border-none',
        text: 'text-accent',
      },
      hover: {
        bg: '!bg-accent-content border-none',
        text: 'text-accent',
      },
      disabled: {
        bg: '!bg-transparent pointer-events-none',
        text: 'text-accent !text-opacity-40',
      },
    },
  },
};
